import { useContext, useEffect, useRef, useState } from 'react';
import { CameraIcon, TrashIcon } from '@heroicons/react/24/outline';
import Avatar from '../../../components/Profile/elements/Avatar';
import { UserContext } from '../../../utils/contexts/UserContext';
import { deleteAvatar, uploadAvatar } from '../../../services/Auth/authService';

const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
const maxBytes = 5 * 1024 * 1024;

export default function AvatarEditor() {
  const { user, setUser } = useContext(UserContext);
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const chooseFile = (event) => {
    const selected = event.target.files?.[0];
    setMessage(null);
    if (!selected) return;
    if (!allowedTypes.includes(selected.type)) {
      setFile(null);
      setMessage({ error: true, text: 'Choose a JPEG, PNG, or WebP image.' });
      return;
    }
    if (selected.size > maxBytes) {
      setFile(null);
      setMessage({ error: true, text: 'The image must be 5 MB or smaller.' });
      return;
    }
    setFile(selected);
  };

  const save = async () => {
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const updated = await uploadAvatar(file);
      setUser((current) => ({ ...current, ...updated }));
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setMessage({ error: false, text: 'Profile photo updated.' });
    } catch (error) {
      const detail = error?.response?.data?.avatar?.[0] || error?.response?.data?.detail;
      setMessage({ error: true, text: detail || 'The profile photo could not be uploaded.' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete your profile photo?')) return;
    setBusy(true);
    setMessage(null);
    try {
      const updated = await deleteAvatar();
      setUser((current) => ({ ...current, ...updated }));
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setMessage({ error: false, text: 'Profile photo deleted.' });
    } catch (error) {
      setMessage({ error: true, text: 'The profile photo could not be deleted.' });
    } finally {
      setBusy(false);
    }
  };

  const currentAvatar = preview || user?.avatar_url || user?.avatar || user?.profileImage;

  return (
    <section aria-labelledby="profile-photo-heading" className="space-y-5">
      <div>
        <h3 id="profile-photo-heading" className="text-lg font-semibold text-gray-900 dark:text-white">
          Profile photo
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
          Use a clear JPEG, PNG, or WebP image up to 5 MB.
        </p>
      </div>
      <div className="flex flex-col items-center gap-5 rounded-2xl border border-gray-200 bg-gray-50 p-6 sm:flex-row dark:border-gray-700 dark:bg-gray-900">
        <Avatar
          name={user?.first_name}
          profileImage={currentAvatar}
          className="h-32 w-32 shrink-0 text-4xl ring-4 ring-white shadow dark:ring-gray-700"
        />
        <div className="w-full space-y-3">
          <input
            ref={inputRef}
            id="avatar-upload"
            className="sr-only"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={chooseFile}
          />
          <div className="flex flex-wrap gap-2">
            <label htmlFor="avatar-upload" className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
              <CameraIcon className="h-5 w-5" />
              {user?.avatar_url ? 'Choose a new photo' : 'Choose photo'}
            </label>
            {file && (
              <button type="button" disabled={busy} onClick={save} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                {busy ? 'Uploading...' : 'Upload photo'}
              </button>
            )}
            {user?.avatar_url && (
              <button type="button" disabled={busy} onClick={remove} className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950">
                <TrashIcon className="h-5 w-5" /> Delete photo
              </button>
            )}
          </div>
          {file && <p className="text-sm text-gray-600 dark:text-gray-300">Selected: {file.name}</p>}
          {message && <p role="status" className={`text-sm ${message.error ? 'text-red-600' : 'text-emerald-600'}`}>{message.text}</p>}
        </div>
      </div>
    </section>
  );
}
